import React, { useState } from 'react';
import styles from './ContactForm.module.css';
import { useForm } from 'react-hook-form';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { ContactFormData } from '../../../types';
import { Button } from '../../common/Button';
import clsx from 'clsx';

export const ContactForm: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>();

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await addDoc(collection(db, 'contact-submissions'), {
        ...data,
        submittedAt: serverTimestamp(),
        status: 'new',
      });

      setSubmitStatus('success');
      reset();

      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.form} noValidate>
      {/* Name Field */}
      <div className={styles.formGroup}>
        <label htmlFor="name" className={styles.label}>
          Name <span className={styles.required} aria-label="required">*</span>
        </label>
        <input
          id="name"
          type="text"
          className={clsx(styles.input, errors.name && styles.inputError)}
          aria-invalid={errors.name ? 'true' : 'false'}
          aria-describedby={errors.name ? 'name-error' : undefined}
          {...register('name', {
            required: 'Name is required',
            minLength: { value: 2, message: 'Name must be at least 2 characters' },
          })}
        />
        {errors.name && (
          <span id="name-error" className={styles.error} role="alert">
            {errors.name.message}
          </span>
        )}
      </div>

      {/* Email Field */}
      <div className={styles.formGroup}>
        <label htmlFor="email" className={styles.label}>
          Email <span className={styles.required} aria-label="required">*</span>
        </label>
        <input
          id="email"
          type="email"
          className={clsx(styles.input, errors.email && styles.inputError)}
          aria-invalid={errors.email ? 'true' : 'false'}
          aria-describedby={errors.email ? 'email-error' : undefined}
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Please enter a valid email address',
            },
          })}
        />
        {errors.email && (
          <span id="email-error" className={styles.error} role="alert">
            {errors.email.message}
          </span>
        )}
      </div>

      {/* Company Field */}
      <div className={styles.formGroup}>
        <label htmlFor="company" className={styles.label}>
          Company (Optional)
        </label>
        <input
          id="company"
          type="text"
          className={styles.input}
          {...register('company')}
        />
      </div>

      {/* Project Type Field */}
      <div className={styles.formGroup}>
        <label htmlFor="projectType" className={styles.label}>
          What's this about? <span className={styles.required} aria-label="required">*</span>
        </label>
        <select
          id="projectType"
          className={clsx(styles.select, errors.projectType && styles.inputError)}
          aria-invalid={errors.projectType ? 'true' : 'false'}
          aria-describedby={errors.projectType ? 'projectType-error' : undefined}
          {...register('projectType', { required: 'Please select an option' })}
        >
          <option value="">Select an option...</option>
          <option value="app-improvement">App improvement</option>
          <option value="new-app">New app project</option>
          <option value="consultation">Employment / collaboration</option>
          <option value="other">Acquisition / other</option>
        </select>
        {errors.projectType && (
          <span id="projectType-error" className={styles.error} role="alert">
            {errors.projectType.message}
          </span>
        )}
      </div>

      {/* Message Field */}
      <div className={styles.formGroup}>
        <label htmlFor="message" className={styles.label}>
          Message <span className={styles.required} aria-label="required">*</span>
        </label>
        <textarea
          id="message"
          rows={5}
          className={clsx(styles.textarea, errors.message && styles.inputError)}
          aria-invalid={errors.message ? 'true' : 'false'}
          aria-describedby={errors.message ? 'message-error' : undefined}
          {...register('message', {
            required: 'Message is required',
            minLength: { value: 10, message: 'Message must be at least 10 characters' },
          })}
        />
        {errors.message && (
          <span id="message-error" className={styles.error} role="alert">
            {errors.message.message}
          </span>
        )}
      </div>

      {/* Submit Button */}
      <div className={styles.submitWrapper}>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </Button>
      </div>

      {/* Status Messages */}
      {submitStatus === 'success' && (
        <div className={styles.successMessage} role="alert" aria-live="polite">
          <p>Message sent — I'll be in touch soon.</p>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className={styles.errorMessage} role="alert" aria-live="assertive">
          <p>Something went wrong. Please try again or email me directly at dixonappdevelopment@gmail.com.</p>
        </div>
      )}
    </form>
  );
};
